import { useState, useCallback } from 'react';
import { Button, Spinner, type ButtonProps } from '@chakra-ui/react';
import { openPdfInNewTab, type DocumentType, type DocumentComponentProps } from './utils';
import { djs } from '@duke-hq/djs';

type Props = {
  documentType: DocumentType;
  children: React.ReactNode;
  enrolmentId: number;
  tenantId?: number;
  semester?: number;
  procedureQuery: (
    args: {
      documentType: DocumentType;
      enrolmentId: number;
      tenantId?: number;
      semester?: number;
    },
    options: { enabled: boolean; retry: boolean },
  ) => { refetch: () => Promise<{ data?: DocumentComponentProps }> };
} & ButtonProps;

export function EnrolmentDocumentButtonAsync({
  documentType,
  children,
  enrolmentId,
  procedureQuery,
  tenantId,
  semester,
  ...buttonProps
}: Props): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const userUTCOffset = djs().utcOffset(); // Get the user's UTC offset in minutes

  const input = {
    documentType,
    enrolmentId,
    semester,
    userUTCOffset,
    ...(tenantId != null ? { tenantId } : {}),
    ...(semester != null ? { semester } : {}),
  };

  const { refetch } = procedureQuery(input, {
    enabled: false,
    retry: false,
  });

  const handleGeneration = useCallback(async () => {
    setLoading(true);
    const newWindow = !window.ReactNativeWebView ? window.open('', '_blank') : null;

    try {
      const { data } = await refetch();
      if (data !== undefined) {
        await openPdfInNewTab(documentType, data, newWindow);
      } else if (newWindow && !newWindow.closed) {
        newWindow.close();
      }
    } catch {
      if (newWindow && !newWindow.closed) {
        newWindow.close();
      }
    } finally {
      setLoading(false);
    }
  }, [refetch, documentType]);

  return (
    <Button
      {...buttonProps}
      onClick={handleGeneration}
      isDisabled={loading}
      rightIcon={loading ? <Spinner ml="2px" size="xs" /> : buttonProps.rightIcon}
    >
      {children}
    </Button>
  );
}

export default EnrolmentDocumentButtonAsync;
